import type { CartGetResult } from "@/dao/cart";
import type { ProductBrowseResult, ProductGetResult } from "@/dao/product";
import { formatMoney } from "@/lib/currencies";
import { HighPrecisionNumber } from "@/lib/financial/high-precision-number";
import { bigIntMax, bigIntMin } from "@/lib/utils";

export const PERCENT_100_INT = 1000_00n;

export const grossFromNet = (net: HighPrecisionNumber, taxRate: string | bigint) =>
	net.multiply(PERCENT_100_INT + BigInt(taxRate)).divide(PERCENT_100_INT);
export const netFromGross = (gross: HighPrecisionNumber, taxRate: string | bigint) =>
	gross.multiply(PERCENT_100_INT).divide(PERCENT_100_INT + BigInt(taxRate));
export const taxFromNet = (net: HighPrecisionNumber, taxRate: string | bigint) =>
	grossFromNet(net, taxRate).subtract(net);
export const taxFromGross = (gross: HighPrecisionNumber, taxRate: string | bigint) =>
	gross.subtract(netFromGross(gross, taxRate));

export type CartLineItem = CartGetResult["lineItems"][number] & {
	itemDiscountGross?: never;
	itemDiscountNet?: never;
	discountedItemPriceGross?: never;
};
export type CartLineItemWithDiscount = CartGetResult["lineItems"][number] & {
	itemDiscountGross: HighPrecisionNumber;
	itemDiscountNet: HighPrecisionNumber;
	discountedItemPriceGross: HighPrecisionNumber;
};

/**
 * Unfold a cart line item with quantity into multiple items with quantity 1.
 */
const unfoldCartLines = (lines: CartLineItem[]) => {
	return lines.flatMap((line) => Array.from({ length: line.quantity }).map(() => ({ ...line, quantity: 1 })));
};

export const getLinesWithDiscountGross = (cart: CartGetResult): CartLineItemWithDiscount[] => {
	const subtotalGross = calculateCartSubtotalGross(cart);
	// console.log({ subtotalGross });

	// Proportional discount allocation method
	// 1. Calculate the coupon value proportionally to the line price to the subtotal
	// 2. Round down to the nearest cent
	// 3. Calculate the remaining coupon value after rounding down for all line items
	// 4. Add the remaining coupon value to the last item
	// -----
	// # Naive method:
	// Take a part of couponValue proportionally to the line gross price to the subtotal gross
	// eg. 2 items: 40zł and 60zł; discount: 50zł
	// 10% tax on each
	// gross prices: 44zł and 66zł
	// subtotal gross: 110zł; expected result 110zł - 50zł = 60zł
	// ------
	// # Calculations in integers
	// (We multiply by 10000 at the beginning and divide by 10000 at the end to avoid integer rounding errors)
	// pricesGross / subtotalGross = ((10000 * 44_00) / 110_00) = 40_00 and ((10000 * 66_00) / 110_00) = 60_00
	// * couponValue = 40_00 * 50_00 / 10000 = 20_00 and 60_00 * 50_00 / 10000 = 30_00
	// The final prices are 44_00 - 20_00 = 24_00 and 66_00 - 30_00 = 36_00
	// So 24zł and 36zł
	// The sum of discounts is correct: 110zł - 50zł = 24zł + 36zł = 60zł
	// ------
	// # Where does the naive method fail?
	// eg. take 3 items: 10zł each; discount: 10zł
	// 0% tax on each to simplify
	// subtotal: 30zł; expected result: 20zł
	// ------
	// pricesGross / subtotalGross = ((10000 * 10_00) / 30_00) = 33_33n
	// * couponValue = 33_33 * 10_00 / 10000 = 3_33
	// The final prices are 10_00 - 3_33 = 6_67
	// So 6.67zł; but the cart total is 20.01zł – unexpectedly
	// The sum of discounts is incorrect: 3.33zł * 3 = 9.99zł
	// ------
	// # Hamilton's Method
	// In order to prevent that, we treat the last item as a special case
	// We add the leftover coupon to the last item
	// So the first two items are priced like we calculated 10_00 - 3_33 = 6_67
	// Now we calculate leftover coupon value: 10_00 - 3_33 * 2 = 3_34
	// And we apply that amount of discount to the last item: 10_00 - 3_34 = 6_66
	// The final prices are 6.67zł + 6.67zł + 6.66zł = 20.00zł
	// The sum of discounts is correct: 3.33zł + 3.33zł + 3.34zł = 10.00zł

	const coupon = cart.customer?.coupon ?? cart.coupon;

	if (coupon?.type === "fixed") {
		const couponValue = BigInt(coupon.value);
		const { discountedLines } = unfoldCartLines(cart.lineItems).reduce(
			(acc, line, index, lines) => {
				const isLast = index === lines.length - 1;
				const priceGross = grossFromNet(
					HighPrecisionNumber.fromBigInt(BigInt(line.productVariant.price)),
					line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
				).round();
				if (isLast) {
					// add leftover coupon to the last item
					acc.discountedLines.push({
						...line,
						itemDiscountGross: acc.couponLeft.round(),
						itemDiscountNet: netFromGross(
							acc.couponLeft.round(),
							line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
						).round(),
						discountedItemPriceGross: priceGross.subtract(acc.couponLeft).round(),
					});
					console.log({
						isLast,
						priceGross,
						itemDiscountGross: acc.couponLeft.round(),
						discountedItemPriceGross: priceGross.subtract(acc.couponLeft).round(),
					});
					acc.couponLeft = HighPrecisionNumber.ZERO;
				} else {
					const itemDiscount = priceGross.divide(subtotalGross).multiply(couponValue);
					acc.discountedLines.push({
						...line,
						itemDiscountGross: itemDiscount,
						itemDiscountNet: netFromGross(
							itemDiscount,
							line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
						).round(),
						discountedItemPriceGross: priceGross.subtract(itemDiscount).round(),
					});
					const couponToApply = itemDiscount.multiply(BigInt(line.quantity)).round();
					acc.couponLeft = acc.couponLeft.subtract(couponToApply).round();
					console.log({
						isLast,
						priceGross,
						itemDiscountGross: itemDiscount,
						couponLeft: acc.couponLeft.round(),
						discountedItemPriceGross: priceGross.subtract(couponToApply).round(),
					});
				}

				return acc;
			},
			{
				couponLeft: HighPrecisionNumber.fromBigInt(couponValue),
				discountedLines: [] as CartLineItemWithDiscount[],
			},
		);
		return discountedLines;
	}

	if (coupon?.type === "percentage") {
		const couponValue = coupon.value;
		return cart.lineItems.map((line) => {
			const priceNet = BigInt(line.productVariant.price);
			const priceGross = grossFromNet(
				HighPrecisionNumber.fromBigInt(priceNet),
				line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
			);
			console.log({ priceGross }, line.productVariant.calculatedPrice);

			// złoto
			if (line.productVariant.calculatedPrice) {
				console.log("calculatedPrice");
				const originalPriceGross = grossFromNet(
					HighPrecisionNumber.fromBigInt(BigInt(line.productVariant.originalPrice ?? 0n)),
					line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
				);

				const itemDiscountGross = HighPrecisionNumber.fromBigInt(
					BigInt(
						Math.round(
							Number(couponValue) * Number(originalPriceGross.toBigInt()) * (line.productVariant.weight ?? 1),
						),
					),
				).divide(PERCENT_100_INT);

				const itemDiscountNet = netFromGross(
					itemDiscountGross,
					line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
				);

				const discountedItemPriceGross = priceGross.subtract(itemDiscountGross);
				console.log({
					originalPriceGross: originalPriceGross.toBigInt(),
					couponValue,
					itemDiscountGross: itemDiscountGross.toBigInt(),
					priceGross: priceGross.toBigInt(),
					discountedItemPriceGross: discountedItemPriceGross.toBigInt(),
				});

				return {
					...line,
					itemDiscountGross,
					itemDiscountNet,
					discountedItemPriceGross,
				};
			} else {
				const itemDiscountGross = HighPrecisionNumber.fromBigInt(BigInt(couponValue))
					.multiply(priceGross)
					.divide(PERCENT_100_INT);
				const itemDiscountNet = netFromGross(
					itemDiscountGross,
					line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
				);
				const discountedItemPriceGross = priceGross.subtract(itemDiscountGross);
				return {
					...line,
					itemDiscountGross,
					itemDiscountNet,
					discountedItemPriceGross,
				};
			}
		});
	}

	return cart.lineItems.map((line) => ({
		...line,
		itemDiscountGross: HighPrecisionNumber.ZERO,
		itemDiscountNet: HighPrecisionNumber.ZERO,
		discountedItemPriceGross: grossFromNet(
			HighPrecisionNumber.fromBigInt(BigInt(line.productVariant.price)),
			line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
		),
	}));
};

export const calculateCartLineSubtotal = (
	line: CartLineItem | CartLineItemWithDiscount,
	taxBehavior: "inclusive" | "exclusive",
) => {
	if (taxBehavior === "inclusive") {
		return calculateCartLineSubtotalGross(line);
	} else {
		return calculateCartLineSubtotalNet(line);
	}
};

export const calculateCartLineSubtotalNet = (line: CartLineItem | CartLineItemWithDiscount) => {
	return BigInt(line.quantity) * BigInt(line.productVariant.price);
};

const calculateCartLineSubtotalGross = (line: CartLineItem | CartLineItemWithDiscount) => {
	const gross = grossFromNet(
		HighPrecisionNumber.fromBigInt(BigInt(line.productVariant.price)),
		line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
	);
	return BigInt(line.quantity) * gross.toBigInt();
};

export const calculateCartSubtotal = (cart: CartGetResult, taxBehavior: "inclusive" | "exclusive") => {
	if (taxBehavior === "inclusive") {
		return calculateCartSubtotalGross(cart);
	} else {
		return calculateCartSubtotalNet(cart);
	}
};

export const calculateCartSubtotalNet = (cart: CartGetResult) => {
	return (
		cart.lineItems?.reduce(
			(total, line) => total.add(calculateCartLineSubtotalNet(line)),
			HighPrecisionNumber.ZERO,
		) ?? HighPrecisionNumber.ZERO
	);
};

export const calculateCartSubtotalGross = (cart: CartGetResult) => {
	return (
		cart.lineItems?.reduce(
			(total, line) => total.add(calculateCartLineSubtotalGross(line)),
			HighPrecisionNumber.ZERO,
		) ?? HighPrecisionNumber.ZERO
	);
};

export const calculateShippingTax = (shipping: CartGetResult["shipping"]) => {
	if (!shipping) {
		return HighPrecisionNumber.ZERO;
	}
	return taxFromNet(
		HighPrecisionNumber.fromBigInt(BigInt(shipping?.price)),
		shipping.shippingTaxRate?.taxRate.rate ?? 0n,
	);
};

export const calculateCartLineTaxWithDiscount = (line: CartLineItemWithDiscount) => {
	return HighPrecisionNumber.fromBigInt(BigInt(line.quantity)).multiply(
		taxFromNet(
			netFromGross(
				line.discountedItemPriceGross,
				line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
			),
			line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
		),
	);
};
export const calculateCartLineTax = (line: CartLineItem) => {
	return HighPrecisionNumber.fromBigInt(BigInt(line.quantity)).multiply(
		taxFromNet(
			HighPrecisionNumber.fromBigInt(BigInt(line.productVariant.price)),
			line.productVariant.product.productTaxRate?.taxRate.rate ?? 0n,
		),
	);
};

export const calculateCartTotalTax = (cart: CartGetResult) => {
	const lines = getLinesWithDiscountGross(cart);
	return (
		lines?.reduce((total, line) => {
			const tax = calculateCartLineTaxWithDiscount(line);
			return total.add(tax ?? 0n);
		}, HighPrecisionNumber.ZERO) ?? HighPrecisionNumber.ZERO
	);
};

export const calculateCartTaxBreakdown = (cart: CartGetResult) => {
	// @todo(taxes) minus discounts
	const taxBreakdown = getLinesWithDiscountGross(cart).reduce(
		(breakdown, line) => {
			const taxRate = line.productVariant.product.productTaxRate?.taxRate;
			if (!taxRate) {
				return breakdown;
			}
			const tax = calculateCartLineTaxWithDiscount(line);

			breakdown[taxRate.id] = {
				taxRate,
				tax: (breakdown[taxRate.id]?.tax ?? HighPrecisionNumber.ZERO).add(tax ?? HighPrecisionNumber.ZERO),
			};

			return breakdown;
		},
		{} as Record<
			string,
			{ taxRate: NonNullable<ProductGetResult["productTaxRate"]>["taxRate"]; tax: HighPrecisionNumber }
		>,
	);
	if (cart.shipping?.shippingTaxRate) {
		const shippingTax = calculateShippingTax(cart.shipping);
		taxBreakdown[cart.shipping.shippingTaxRate.taxRate.id] = {
			taxRate: cart.shipping.shippingTaxRate.taxRate,
			tax: (taxBreakdown[cart.shipping.shippingTaxRate.taxRate.id]?.tax ?? HighPrecisionNumber.ZERO).add(
				shippingTax ?? HighPrecisionNumber.ZERO,
			),
		};
	}
	return taxBreakdown;
};

export const calculateSubtotalGrossWithDiscounts = (cart: CartGetResult) => {
	const linesWithDiscount = getLinesWithDiscountGross(cart);
	const subtotalGrossWithDiscounts = linesWithDiscount.reduce((acc, line) => {
		console.log(line.discountedItemPriceGross);
		return acc.add(
			line.discountedItemPriceGross.multiply(HighPrecisionNumber.fromBigInt(BigInt(line.quantity))),
		);
	}, HighPrecisionNumber.ZERO);

	return subtotalGrossWithDiscounts;
};

/**
 * total is always gross
 */
export const calculateCartTotal = (
	cart: CartGetResult,
	freeShippingThreshold_: bigint | null | undefined,
) => {
	// @todo(taxes) add Stripe taxes to the total in the future

	const freeShippingThreshold = freeShippingThreshold_
		? HighPrecisionNumber.fromBigInt(freeShippingThreshold_)
		: null;
	const subtotalGrossWithDiscounts = calculateSubtotalGrossWithDiscounts(cart);
	console.log({ freeShippingThreshold, subtotalGrossWithDiscounts });

	if (freeShippingThreshold && subtotalGrossWithDiscounts >= freeShippingThreshold) {
		return subtotalGrossWithDiscounts;
	}

	const shippingInt = grossFromNet(
		HighPrecisionNumber.fromBigInt(BigInt(cart.shipping?.price ?? 0n)),
		cart.shipping?.shippingTaxRate?.taxRate?.rate ?? 0n,
	);
	return subtotalGrossWithDiscounts.add(shippingInt);
};

export const getVariantsPriceRange = (
	variants: ProductBrowseResult["data"][number]["variants"],
	options?: { originalPrice?: boolean },
) => {
	const prices_ = variants.map(
		(variant) => (options?.originalPrice ? variant.originalPrice : variant.price) ?? 0n,
	);
	const prices = prices_.length > 0 ? prices_ : ["0"];
	const min = bigIntMin(...prices);
	const max = bigIntMax(...prices);
	return { min, max };
};

export const formatVariantsPriceRange = ({
	variants,
	taxRate,
	currency,
	locale,
	taxBehavior,
}: {
	variants: ProductBrowseResult["data"][number]["variants"];
	taxRate: string | bigint;
	currency: string;
	locale: string;
	taxBehavior: "inclusive" | "exclusive";
}) => {
	const { min, max } = getVariantsPriceRange(variants);

	const minPrice = formatPriceWithTax({
		price: min,
		taxBehavior,
		taxRate,
		currency,
		locale,
	});
	const maxPrice = formatPriceWithTax({
		price: max,
		taxBehavior,
		taxRate,
		currency,
		locale,
	});

	return minPrice === maxPrice ? minPrice : `${minPrice} – ${maxPrice}`;
};

export const formatPriceWithTax = ({
	price,
	taxRate,
	currency,
	locale,
	taxBehavior,
}: {
	price: string | bigint;
	taxRate: string | bigint;
	currency: string;
	locale: string;
	taxBehavior: "inclusive" | "exclusive";
}) => {
	const priceInt = HighPrecisionNumber.fromBigInt(BigInt(price));
	const gross = taxBehavior === "inclusive" ? grossFromNet(priceInt, taxRate) : priceInt;

	return formatMoney({
		amount: gross.toBigInt(),
		currency,
		locale,
	});
};
